import React from 'react';
import styled from 'styled-components';
import { GhostButton } from '@ui/buttons';
import ChevronDown from '@assets/icons/svgs/ChevronDown';

// An inline disclosure: a summary you press, and a panel beneath it.
//
// The chevron was character-identical in ColumnChooser and KnowableWindowFilter, down to the
// prefers-reduced-motion block. More usefully, this owns the panel id — which was a MODULE-SCOPE
// CONSTANT in three files (`feed-view-column-toggles`, `feed-view-knowable-window`,
// `feed-view-feed-options`), so none of those components could be mounted twice on one page without
// two elements claiming the same id and `aria-controls` pointing at whichever won.
//
// useId means the id is per-instance, which is what a redesign that renders a filter bar per panel,
// or a chooser in a comparison view, needs.
//
// It does NOT position the panel. Inline, in flow, beneath the trigger — no floating, no
// outside-click handling, no focus trap. KnowableWindowFilter's panel additionally takes a
// flex-basis so it wraps onto its own line of the bar it sits in; that is the caller's layout and
// stays with the caller.
const Trigger = styled(GhostButton)`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const Chevron = styled.span`
  display: inline-flex;
  width: 14px;
  height: 14px;
  transform: rotate(${({ $open }) => ($open ? '0deg' : '-90deg')});
  transition: transform 120ms ease;

  svg {
    width: 14px;
    height: 14px;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

// Returns the trigger and the panel as two siblings rather than wrapping them, because both callers
// need the panel to be a flex item of THEIR container — one to wrap onto its own line of the filter
// bar, the other to sit in the chooser's column. A wrapper here would make both impossible.
// `triggerAs` and `panelAs` exist because the two callers genuinely differ in CHROME while agreeing
// on BEHAVIOUR: the chooser's trigger is bare text, the window filter's is a bordered control that
// sits in a row of bordered controls, and the window filter's panel additionally needs a flex-basis
// so it wraps onto its own line. What is shared is the chevron, the aria wiring and the id — which
// is the part that was duplicated and the part that was broken.
export const Disclosure = ({
  isOpen,
  onToggle,
  summary,
  children,
  triggerAs: TriggerEl = Trigger,
  panelAs: Panel = 'div',
  // Props for the panel element, so a caller that needs to vary its CHROME does not have to vary
  // its TYPE. React compares element types, so a caller building `panelAs` per render remounts the
  // panel — and everything inside it — on every change it was trying to describe.
  panelProps,
}) => {
  const panelId = React.useId();

  return (
    <>
      <TriggerEl onClick={onToggle} aria-expanded={isOpen} aria-controls={panelId}>
        <Chevron $open={isOpen} aria-hidden="true">
          <ChevronDown color="currentColor" />
        </Chevron>
        {summary}
      </TriggerEl>
      {isOpen && (
        <Panel id={panelId} {...panelProps}>
          {children}
        </Panel>
      )}
    </>
  );
};
